import { parseApiDate } from "@/common/answers";
import { getQuestionSetData } from "@/common/question-set";
import {
  ANNUAL,
  D2C_COM_TYPE,
  EXIT_DESTINATION,
} from "@/constants/common.constants";
import { SYSTEM_ERRORS } from "@/constants/message.constants";
import applicationService from "@/services/application.service";
import quoteService from "@/services/quote.service";

export default {
  async loadApplicationSections({ commit, dispatch, rootGetters }) {
    try {
      const qsData = await getQuestionSetData(
        rootGetters["questions/qsId"],
        rootGetters["questions/branchNumber"],
        rootGetters["questions/callChannel"],
        "application"
      );
      commit("SET_SECTIONS", qsData);
      dispatch("setQuestionMap");
      dispatch("steps/initializeApplicationSteps", qsData.Sections, {
        root: true,
      });
    } catch (error) {
      console.error(error);
      throw new Error(SYSTEM_ERRORS.errorRedirectAdviserHub.errorId);
    }
  },
  updateApplication({ dispatch, getters, rootGetters }, applyRequestFields) {
    // Patch in the applyRequestFields to set IsPartialApply
    const applyRequest = {
      ...getters["getApplyRequest"],
      ...applyRequestFields,
    };

    // Always use the Rqs httpConfig for card payments to avoid partial integrator errors
    const config =
      applyRequest.Answers.find((ans) => ans.InterfaceKey === "PaymentMethod")
        ?.Value === "CreditCard"
        ? rootGetters["questions/httpConfigAsRqs"]
        : rootGetters["questions/httpConfig"];

    return applicationService
      .apply(getters.quoteId, applyRequest, config)
      .then((data) => {
        dispatch("updateApplyResponse", data);
        return data;
      })
      .catch((error) => {
        throw error; // Rethrow for downstream handlers
      });
  },
  handleApplicationSubmission({ dispatch }) {
    const applyRequestFields = {
      IsPartialApply: false,
    };
    return dispatch("updateApplication", applyRequestFields);
  },
  handleSaveAndExit({ commit, dispatch }) {
    const applyRequestFields = {
      IsPartialApply: true,
    };

    return dispatch("updateApplication", applyRequestFields).then(() => {
      commit("EXIT_TO_ADVISERHUB");
    });
  },
  handleD2CSaveAndExit(
    { commit, dispatch, getters, rootGetters },
    exitDestination = EXIT_DESTINATION.d2cHomePage
  ) {
    const applyRequestFields = {
      IsPartialApply: true,
    };

    return dispatch("updateApplication", applyRequestFields)
      .then(() => dispatch("addSelectedQuoteInfo", getters["selectedQuote"]))
      .then((selectedQuoteModel) => {
        dispatch(
          "communication/send",
          {
            type: D2C_COM_TYPE.quoteReference,
            quoteReference: rootGetters["questions/quoteReference"],
            selectedQuote: selectedQuoteModel,
          },
          {
            root: true,
          }
        )
          .catch((err) => console.error(err))
          .finally(() => {
            commit(exitDestination);
          });
      });
  },
  addSelectedQuoteInfo({ rootGetters, getters }, selectedQuote) {
    selectedQuote.ExcessBuildings =
      rootGetters["questions/getNumericAnswer"]("ExcessBuildings");
    selectedQuote.ExcessContents =
      rootGetters["questions/getNumericAnswer"]("ExcessContents");
    selectedQuote.FirstName =
      rootGetters["questions/getAnswer"]("Applicant1Forename");
    selectedQuote.PaymentMethod = getters.allAnswers.find(
      (ans) => ans.InterfaceKey === "PaymentMethod"
    )?.Value;
    selectedQuote.PaymentFrequency = getters.allAnswers.find(
      (ans) => ans.InterfaceKey === "PaymentFrequency"
    )?.Value;
    if (!selectedQuote.PaymentFrequency) {
      //the default payment frequency is ANNUAL if user not yet chosen any option
      //at application step
      selectedQuote.PaymentFrequency = ANNUAL;
    }

    return selectedQuote;
  },
  handleSaveSDNs({ rootGetters }, submission) {
    const config = rootGetters["questions/httpConfig"];
    const quoteRequestId = rootGetters["questions/getQuoteRequestId"];

    return applicationService
      .saveSDNs(quoteRequestId, submission, config)
      .then((data) => {
        return data;
      })
      .catch((error) => {
        throw error; // Rethrow for downstream handlers
      });
  },
  updateApplyResponse({ commit }, response) {
    commit("UPDATE_APPLY_RESPONSE", response);
    // Update quote status to Submitted upon successful application:
    commit("questions/SET_QUOTE_STATUS", response.QuoteStatus, {
      root: true,
    });
  },
  setQuestionVisibility({ commit, getters }, question) {
    commit("SET_QUESTION_VISIBILITY", { getters, question });
  },
  setApplicationActiveQuote({ commit }, activeQuote) {
    commit("SET_ACTIVE_QUOTE", activeQuote);
  },
  setSessionStorageItems({ state, rootState, getters }) {
    sessionStorage.setItem("quoteRequestId", getters.quoteRequestId);
    sessionStorage.setItem(
      "paymentMethod",
      state.questionMap.PaymentMethod.Value
    );
    sessionStorage.setItem(
      "commissionSacrifice",
      rootState.questions.commissionSacrifice
    );
    sessionStorage.setItem("activeQuote", getters.getActiveQuoteObjectAsString);
  },
  proceedToCardPayment({ getters }) {
    const cardPaymentDetails = getters.cardPaymentContinuationDetails;
    if (!cardPaymentDetails?.CardPaymentContinuationUri)
      throw new Error(
        "The card payment details are either missing or incomplete in the apply response"
      );

    if (cardPaymentDetails.RedirectParams) {
      // Silver Lining route (Advised/Internal Sales)
      const form = document.createElement("form");
      form.setAttribute(
        "action",
        cardPaymentDetails.CardPaymentContinuationUri
      );
      form.setAttribute("method", "post");

      document.body.appendChild(form);

      for (const [key, value] of Object.entries(
        cardPaymentDetails.RedirectParams
      )) {
        const input = document.createElement("input");
        input.setAttribute("type", "hidden");
        input.setAttribute("name", key);
        input.setAttribute("value", value);
        form.appendChild(input);
      }

      window.onbeforeunload = null;
      form.submit();
    } else {
      // Secure Hosting route (external brokers)
      window.onbeforeunload = null;
      window.location.href = cardPaymentDetails.CardPaymentContinuationUri;
    }
  },
  retrieveQuoteByQuoteRequestIdFollowingSuccessfulCardPayment({
    state,
    dispatch,
    rootGetters,
  }) {
    const quoteRequestId = sessionStorage.getItem("quoteRequestId");
    const config = rootGetters["questions/httpConfig"];
    return retrieveQuote(dispatch, state, config, quoteRequestId);
  },
  retrieveQuoteByQuoteRequestIdFollowingCancelledCardPayment(
    { state, dispatch, rootGetters },
    quoteRequestId
  ) {
    const config = rootGetters["questions/httpConfig"];
    return retrieveQuote(dispatch, state, config, quoteRequestId);
  },
  loadAnswers({ commit, rootGetters }, data) {
    commit("LOAD_RETRIEVED_QUOTE_DATA", { data, rootGetters });
  },
  repopulateActiveQuoteFromSessionStorage({ commit }) {
    const activeQuoteFromSessionStorage = JSON.parse(
      sessionStorage.getItem("activeQuote")
    );
    commit("SET_ACTIVE_QUOTE", activeQuoteFromSessionStorage);
  },
  repopulateCommissionSacrifice({ dispatch }) {
    const commissionSacrifice = parseFloat(
      sessionStorage.getItem("commissionSacrifice")
    );
    dispatch("questions/updateCommissionSacrifice", commissionSacrifice, {
      root: true,
    });
  },
  loadDummyData({ commit, rootGetters }, data) {
    commit("LOAD_RETRIEVED_QUOTE_DATA", { data, rootGetters });
  },
  setCardPaymentStatus({ commit }, payload) {
    commit("SET_CARD_PAYMENT_STATUS", payload);
  },
  async updateSelectedQuote({ getters, rootGetters, rootState, dispatch }) {
    const dateOfBirth = parseApiDate(
      rootState.questions.questionMap.Applicant1DoB?.Value
    );

    const postCode =
      rootState.questions.questionMap.InsuredAddressPostcode?.Value;

    const data = {
      QuoteRequestId: rootGetters["questions/getQuoteRequestId"],
      QuoteReference: rootGetters["questions/quoteReference"],
      SelectedQuote: await dispatch(
        "addSelectedQuoteInfo",
        getters["selectedQuote"]
      ),
      DateOfBirth: dateOfBirth,
      PostCode: postCode,
    };
    return quoteService
      .updateSelectedQuote(data, rootGetters["questions/httpConfig"])
      .catch((err) => console.error(err));
  },
  setQuestionMap({ getters, commit }) {
    const questionMap = getters.allQuestions.reduce((questionMap, question) => {
      // if the current section has SubSections
      if (question.InterfaceKey) {
        questionMap[question.InterfaceKey] = question;
      }
      return questionMap;
    }, {});

    commit("SET_QUESTION_MAP", questionMap);
  },
  setPaymentErrorMessage({ commit }, payload) {
    commit("SET_PAYMENT_ERROR_MESSAGE", payload);
  },
  clearPaymentErrorMessage({ commit }, payload) {
    commit("CLEAR_PAYMENT_ERROR_MESSAGE", payload);
  },
  resetInvalidQuestions({ commit }, errors) {
    commit("RESET_INVALID_QUESTIONS", { errors });
  },
};
function retrieveQuote(dispatch, state, config, quoteRequestId) {
  const getQuotePromise = config.headers?.QuoteAccessId
    ? quoteService.retrieveQuoteByAccessId(config.headers.QuoteAccessId, config)
    : quoteService.retrieveQuote(quoteRequestId, config);

  return getQuotePromise
    .then(async (data) => {
      if (!state.form.sections.length) {
        await Promise.all([
          restoreQuestionSet(dispatch, data),
          restoreSections(dispatch, data),
        ]);
      }
      await Promise.all([
        dispatch("loadAnswers", data.Answers),
        dispatch("updateApplyResponse", data),
        dispatch("questions/updateQuoteResponse", data, { root: true }),
        dispatch("questions/loadAnswers", data.Answers, {
          root: true,
        }),
      ]);
    })
    .catch((err) => {
      console.log(err);
    });
}

async function restoreQuestionSet(dispatch, data) {
  await dispatch(
    "questions/setBranchChannelAndQsid",
    {
      branchNumber: data.BranchNumber,
      callChannel: data.CallChannelType,
      questionSetId: data.QuestionSetId,
    },
    {
      root: true,
    }
  );
}

async function restoreSections(dispatch) {
  await dispatch("questions/loadQuestionsSections", null, {
    root: true,
  });
  await dispatch("loadApplicationSections");
}
